import { EuiConfirmModal } from "@inscopix/ideas-eui";
import assert from "assert";
import { ButtonEmptyPermissioned } from "components/ButtonEmptyPermissioned/ButtonEmptyPermissioned";
import { Flyout } from "components/Flyout/Flyout";
import { MarkdownEditor } from "components/MarkdownEditor/MarkdownEditor";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";
import { useState } from "react";
import { isDefined } from "utils/isDefined";

interface FlyoutTableDescriptionProps {
  onClose: () => void;
}

export const FlyoutTableDescription = ({
  onClose,
}: FlyoutTableDescriptionProps) => {
  const [edit, setEdit] = useState(false);
  const selectedTableId = useDataTableContext((s) => s.selectedTableId);
  const selectedTableDescription = useDataTableContext((s) => {
    const table = s.tables.find((table) => table.id === selectedTableId);
    assert(isDefined(table));
    return table.description;
  });
  const selectedTableKind = useDataTableContext((s) => {
    const table = s.tables.find((table) => table.id === selectedTableId);
    assert(isDefined(table));
    return table.kind;
  });
  const updateTable = useDataTableContext((s) =>
    selectedTableKind === "analysis"
      ? s.updateAnalysisTable
      : s.updateDataTable,
  );
  const isEditingPermitted = useProjectPermissionStore(
    (s) => s.permissions.edit,
  );

  const onSave = async (value: string) => {
    assert(isDefined(selectedTableId));
    await updateTable({ tableId: selectedTableId, description: value });
  };

  // intercept user from closing the flyout if they are editing
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const closeConfirmModal = (
    <EuiConfirmModal
      onCancel={() => setShowConfirmModal(false)}
      onConfirm={onClose}
      title="Close without saving?"
      cancelButtonText="Keep editing"
      confirmButtonText="Discard changes"
      buttonColor="danger"
      defaultFocusedButton="confirm"
    >
      You will lose any unsaved changes
    </EuiConfirmModal>
  );

  const onCloseWithConfirmation = () => {
    if (edit) {
      setShowConfirmModal(true);
    } else {
      onClose();
    }
  };

  return (
    <Flyout
      title={<span>Notes</span>}
      headerControls={
        !edit && (
          <ButtonEmptyPermissioned
            onClick={() => {
              setEdit(true);
            }}
            iconType="documentEdit"
            aria-label="Edit notes"
            requiredPermission="edit"
            size="s"
          >
            Edit
          </ButtonEmptyPermissioned>
        )
      }
      onClose={onCloseWithConfirmation}
      titleSpacerSize="s"
    >
      <MarkdownEditor
        edit={edit}
        setEdit={setEdit}
        emptyPrompt={"Add markdown notes for this table"}
        disableEdit={!isEditingPermitted}
        initialValue={selectedTableDescription}
        onSave={onSave}
      />
      {showConfirmModal && closeConfirmModal}
    </Flyout>
  );
};
